import { AfterViewInit, Component, input, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, defer, merge, Observable, of, Subject } from 'rxjs';
import { Store, TypedSelector } from '@ngxs/store';
import { ConfigService, ConfigState, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import {
  applicationDebounceTime,
  BasicPluralPipe,
  combineLatestAsObject,
  COUNTRY_CODE_TO_NOUN_DISPLAY_MAP,
  distinctUntilObjectChanged,
  emitValueIfValid,
  getAOrAn,
  getControlValueChanges,
  getLoadedResults,
  ignoreFalsy,
  ignoreNullish,
  mapToOrResult,
  roundMonetaryValue,
  toggleAbstractControlEnabledState,
} from '@aiwyn/shared';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { GetTotalOptions, PaymentsState, SetCustomProcessingFeeRate, SetPayLateFees } from '../../state';
import { AbstractPaymentMethod, PAYMENT_METHOD_TYPE_DISPLAY, PaymentFlow, PaymentPlatformService } from '../../platforms';
import { AuthAdaptorService } from '@aiwyn/auth';
import { Currency, DEFAULT_CURRENCY, PaymentFeeType, PaymentMethodType } from '@aiwyn/api';
import { toObservable } from '@angular/core/rxjs-interop';
import { PercentPipe } from '@angular/common';
import { sum } from 'lodash';

type DynamicPricingOptions = [boolean, AbstractPaymentMethod, GetTotalOptions];

@Component({
  selector: 'app-payment-summary',
  templateUrl: './payment-summary.component.html',
  styleUrls: ['./payment-summary.component.scss'],
  standalone: false,
})
export class PaymentSummaryComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() overriddenSelectedPaymentMethod$: Observable<AbstractPaymentMethod>;
  @Input() overriddenSubtotal$: Observable<number>;
  @Input() paymentFlow: PaymentFlow = PaymentFlow.STANDARD;
  @Input() isCustomProcessingFeeEnabled: boolean = false;

  // TODO: We may want to update how this is used in the initial getters (subtotal$, processingFeeRate$, etc.) to make our data pipeline
  //  more streamlined by incorporating this into the returned observables instead of just looking at a snapshot of the value directly.
  forceZeroState$ = new BehaviorSubject<boolean>(undefined);
  @Input() set forceZeroState(forceZeroState: boolean) {
    emitValueIfValid(forceZeroState, this.forceZeroState$);
  }

  ignoreLateFees$ = new BehaviorSubject<boolean>(undefined);
  @Input() set ignoreLateFees(ignoreLateFees: boolean) {
    emitValueIfValid(ignoreLateFees, this.ignoreLateFees$);
  }

  disableLateFeesMessage = input<string>();
  disableLateFeesMessage$ = toObservable(this.disableLateFeesMessage);

  areLateFeesByClient = input<boolean>();
  areLateFeesByClient$ = toObservable(this.areLateFeesByClient);

  lateFeeTooltipMessage = input<string>();

  currency = input<Currency>(DEFAULT_CURRENCY);
  currency$ = toObservable(this.currency);

  overriddenBusinessEntityId = input<number>();
  overriddenBusinessEntityId$ = toObservable(this.overriddenBusinessEntityId);

  private readonly _unsubscribe$ = new Subject<void>();

  selectedPaymentMethod$: Observable<AbstractPaymentMethod>;
  subtotal$: Observable<number>;

  getTotalOptions$ = defer(() => combineLatestAsObject({
    subtotal: this.subtotal$,
    ignoreLateFees: combineLatest([this.disableLateFeesMessage$, this.ignoreLateFees$]).pipe(mapToOrResult()),
    currencyCode: this.currency$.pipe(map(({ code }) => code)),
    businessEntityId: this.overriddenBusinessEntityId$,
    isPaymentPlan: of(this.paymentFlow === PaymentFlow.PLAN),
  })).pipe(
    shareReplay(1),
  );

  dynamicPricingOptions$: Observable<DynamicPricingOptions> = defer(() => combineLatest([
    this.forceZeroState$,
    this.selectedPaymentMethod$,
    this.getTotalOptions$,
  ]));

  payLateFeesByClient$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES);
  lateFeeLabel$ = this.payLateFeesByClient$.pipe(
    map(payLateFeesByClient =>
      payLateFeesByClient
        ? BasicPluralPipe.staticTransform(this.configService.lateFeeDisplayName)
        : this.configService.lateFeeDisplayName,
    ),
  );
  lateFeesExist$ = this.store.select(PaymentsState.getLateFees).pipe(
    map(lateFees => lateFees.length > 0),
  );
  lateFeesTotal$ = this.areLateFeesByClient$.pipe(
    switchMap(areLateFeesByClient => areLateFeesByClient ? of(undefined) : this.store.select(PaymentsState.getLateFeesTotal)),
  );
  payAllLateFees$ = this.store.select(PaymentsState.getPayAllLateFees);

  processingFeeRate$: Observable<number>;
  processingFee$ = this.dynamicPricingOptions$.pipe(
    switchMap(([forceZeroState, selectedPaymentMethod, getTotalOptions]) =>
      forceZeroState ? of(0) : this.store.select(PaymentsState.getProcessingFee(selectedPaymentMethod, getTotalOptions)),
    ),
  );

  internationalFeeRate$ = defer(() => combineLatest([
    this.selectedPaymentMethod$,
    this.currency$,
  ])).pipe(
    switchMap(([selectedPaymentMethod, currency]) =>
      this.store.select(this._getPaymentFeeRateSelector(selectedPaymentMethod, currency, PaymentFeeType.INTERNATIONAL_FEE_RATE)),
    ),
    shareReplay(1),
  );
  internationalFee$ = this.dynamicPricingOptions$.pipe(
    switchMap(([, selectedPaymentMethod, getTotalOptions]) =>
      this.store.select(PaymentsState.getInternationalFee(selectedPaymentMethod, getTotalOptions)),
    ),
  );
  businessEntityCountry$ = this.overriddenBusinessEntityId$.pipe(
    switchMap(businessEntityId =>
      businessEntityId
        ? of(businessEntityId)
        : this.store.select(PaymentsState.getSelectedBusinessEntity(true)).pipe(
          map(businessEntity => businessEntity?.id),
        ),
    ),
    switchMap(businessEntityId => getLoadedResults(this.store.select(ConfigState.getBusinessEntityLoadable(businessEntityId)))),
    map(businessEntity => COUNTRY_CODE_TO_NOUN_DISPLAY_MAP[businessEntity.countryCode]),
  );
  percentPipe = new PercentPipe('en-US');
  internationalFeeRateTooltipMessage$ = defer(() => combineLatest([
    this.internationalFeeRate$,
    this.selectedPaymentMethod$.pipe(ignoreNullish()),
    this.businessEntityCountry$,
  ])).pipe(
    map(([internationalFeeRate, selectedPaymentMethod, businessEntityCountry]) => {
      const rateIndefiniteArticle = getAOrAn(PaymentSummaryComponent.formatRate(internationalFeeRate), { capitalize: true });
      const anInternationalFee = `${rateIndefiniteArticle} ${this.percentPipe.transform(internationalFeeRate, '1.0-2')} international fee`;

      if (selectedPaymentMethod.type === PaymentMethodType.CARD) {
        const cardType = `${selectedPaymentMethod.subType} card`;
        return `${anInternationalFee} is applied when paying with a ${cardType} issued outside of ${businessEntityCountry}.`;
      }

      const paymentMethodTypeDisplay = PAYMENT_METHOD_TYPE_DISPLAY[selectedPaymentMethod.type];
      return `${anInternationalFee} is applied when paying from outside ${businessEntityCountry} with a ${paymentMethodTypeDisplay}.`;
    }),
  );

  total$ = this.dynamicPricingOptions$.pipe(
    switchMap(([forceZeroState, selectedPaymentMethod, getTotalOptions]) =>
      forceZeroState ? of(0) : this.store.select(PaymentsState.getTotal(selectedPaymentMethod, getTotalOptions)),
    ),
  );

  customProcessingFeeRateControl = new UntypedFormControl(undefined, [Validators.required, Validators.min(0)]);
  customProcessingFeeControl = new FormControl<number>(undefined, [Validators.required, Validators.min(0)]);

  shouldApplyCustomProcessingFee$ = combineLatest([
    this.processingFee$,
    this.store.select(PaymentsState.getCustomProcessingFeeRate),
  ]).pipe(
    map(([processingFee, customProcessingFeeRate]) => !!processingFee || customProcessingFeeRate === 0),
    distinctUntilChanged(),
    shareReplay(1),
  );

  subtotalForProcessingFee$ = defer(() => combineLatest([
    this.subtotal$.pipe(
      map(subtotal => subtotal ?? 0),
    ),
    this.areLateFeesByClient$.pipe(
      switchMap(areLateFeesByClient => areLateFeesByClient ? of(0) : this.store.select(PaymentsState.getLateFeesTotal)),
    ),
  ]).pipe(
    map(sum),
    shareReplay(1),
  ));

  updateCustomProcessingFeeRate$ = this.shouldApplyCustomProcessingFee$.pipe(
    tap(shouldApplyProcessingFee => toggleAbstractControlEnabledState(
      { enabled: shouldApplyProcessingFee, emitEvent: false },
      this.customProcessingFeeRateControl,
      this.customProcessingFeeControl,
    )),
    switchMap(shouldApplyProcessingFee => {
      if (!shouldApplyProcessingFee) {
        return of(0);
      }

      return merge(
        // when user updates custom processing fee control
        combineLatest([
          this.subtotalForProcessingFee$,
          this.customProcessingFeeControl.valueChanges.pipe(
            ignoreFalsy(),
            distinctUntilChanged(),
          ),
        ]).pipe(
          applicationDebounceTime(),
          map(([subtotal, customProcessingFee]) => PaymentSummaryComponent.fromFeeToRate(+customProcessingFee, subtotal)),
          tap(customProcessingFeeRate => this.store.dispatch(new SetCustomProcessingFeeRate(customProcessingFeeRate))),
        ),

        // when payment fee config rate changes (from payment detail changes)
        this.store.select(PaymentsState.getCustomProcessingFeeRate).pipe(
          switchMap(customProcessingFeeRate => {
            if (customProcessingFeeRate != undefined) {
              return of(customProcessingFeeRate);
            }

            return combineLatest([this.selectedPaymentMethod$, this.currency$]).pipe(
              map(([selectedPaymentMethod, currency]) =>
                this.store.selectSnapshot(this._getPaymentFeeRateSelector(selectedPaymentMethod, currency)),
              ),
              tap(_ => this.store.dispatch(new SetCustomProcessingFeeRate(undefined))),
            );
          }),
        ),
      );
    }),
    switchMap(processingFeeRate => this.subtotalForProcessingFee$.pipe(
      take(1),
      tap(subtotal => {
        const formattedProcessingFeeRate = PaymentSummaryComponent.formatRate(processingFeeRate);
        this.customProcessingFeeRateControl.setValue(formattedProcessingFeeRate, { emitEvent: false });
        this.setCustomProcessingFeeControl(processingFeeRate, subtotal);
      }),
    )),
    takeUntil(this._unsubscribe$),
  );

  onCustomProcessingFeeRateManualChange$ = defer(() => combineLatest([
    this.subtotalForProcessingFee$,
    getControlValueChanges<string>(this.customProcessingFeeRateControl).pipe(
      ignoreFalsy(),
      map(customProcessingFeeRate => PaymentSummaryComponent.unformatRate(+customProcessingFeeRate)),
    ),
    this.shouldApplyCustomProcessingFee$,
  ]).pipe(
    filter(([, , shouldApplyCustomProcessingFee]) => shouldApplyCustomProcessingFee),
    distinctUntilObjectChanged(),
    applicationDebounceTime(),
    tap(([subtotal, customProcessingFeeRate]) => {
      this.setCustomProcessingFeeControl(customProcessingFeeRate, subtotal);
      this.store.dispatch(new SetCustomProcessingFeeRate(customProcessingFeeRate));
    }),
    takeUntil(this._unsubscribe$),
  ));

  updateCustomProcessingFeeOnSubtotalChange$ = combineLatest([
    this.subtotalForProcessingFee$.pipe(distinctUntilChanged()),
    this.shouldApplyCustomProcessingFee$,
  ]).pipe(
    filter(([, shouldApplyCustomProcessingFee]) => shouldApplyCustomProcessingFee),
    tap(([subtotal]) => this.setCustomProcessingFeeControl(
      PaymentSummaryComponent.unformatRate(this.customProcessingFeeRateControl.value),
      subtotal,
    )),
    takeUntil(this._unsubscribe$),
  );

  static fromRateToFee(rate: number, subtotal: number): number {
    return rate * subtotal;
  }

  static fromFeeToRate(fee: number, subtotal: number): number {
    return fee / subtotal;
  }

  static formatRate(rate: number): number {
    return roundMonetaryValue(rate * 100);
  }

  static unformatRate(rate: number): number {
    return +rate / 100;
  }

  static formatFee(fee: number): number {
    return roundMonetaryValue(fee);
  }

  setCustomProcessingFeeControl(rate: number, subtotal: number): void {
    const customProcessingFee = PaymentSummaryComponent.fromRateToFee(rate, subtotal);
    const formattedCustomProcessingFee = PaymentSummaryComponent.formatFee(customProcessingFee);
    this.customProcessingFeeControl.setValue(formattedCustomProcessingFee, { emitEvent: false });
  }

  constructor(
    private readonly store: Store,
    private readonly paymentPlatformService: PaymentPlatformService,
    readonly configService: ConfigService,
    readonly authAdaptor: AuthAdaptorService,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {}

  /**
   * Build the processing-fee tooltip by injecting the live formatted rate (e.g. "2.4%")
   * into the configurable template's `{rate}` token. The template comes from per-firm
   * config (or the default), so the wording varies but the percentage is always the
   * computed rate. See PP-3776.
   */
  buildProcessingFeeTooltip(formattedRate: string): string {
    return (this.configService.processingFeeTooltip ?? '').split('{rate}').join(formattedRate);
  }

  ngOnInit(): void {
    this.selectedPaymentMethod$ = this._getSelectedPaymentMethod$();
    this.subtotal$ = this._getSubtotal$();

    if (!(this.isCustomProcessingFeeEnabled && this.authAdaptor.isAdmin)) {
      this.processingFeeRate$ = this._getProcessingFeeRate$();
    }
  }

  ngAfterViewInit(): void {
    if (this.isCustomProcessingFeeEnabled && this.authAdaptor.isAdmin) {
      this.customProcessingFeeRateControl.disable({ emitEvent: false });
      this.customProcessingFeeControl.disable({ emitEvent: false });
      this.updateCustomProcessingFeeRate$.subscribe();
      this.onCustomProcessingFeeRateManualChange$.subscribe();
      this.updateCustomProcessingFeeOnSubtotalChange$.subscribe();
    }
  }

  private _getProcessingFeeRate$(): Observable<number> {
    return this.forceZeroState$.pipe(
        switchMap(forceZeroState => {
          if (forceZeroState) {
            return of(0);
          }

          return combineLatest([ this.selectedPaymentMethod$, this.currency$ ]).pipe(
            switchMap(([selectedPaymentMethod, currency]) => this.store.select(this._getPaymentFeeRateSelector(selectedPaymentMethod, currency)))
          );
        })
    );
  }

  private _getSelectedPaymentMethod$(): Observable<AbstractPaymentMethod> {
    return this.forceZeroState$.pipe(
      switchMap(forceZeroState => forceZeroState ? of(undefined) : (this.overriddenSelectedPaymentMethod$ ?? this.paymentPlatformService.getSelectedPaymentMethod()))
    );
  }

  private _getSubtotal$(): Observable<number> {
    return this.forceZeroState$.pipe(
      switchMap(forceZeroState => forceZeroState ? of(0) : (this.overriddenSubtotal$ ?? this.store.select(PaymentsState.getSubtotal)))
    );
  }

  setPayLateFees(payLateFees: boolean): void {
    this.store.dispatch(new SetPayLateFees(payLateFees));
  }

  public validate(): Observable<boolean> {
    if (!(this.isCustomProcessingFeeEnabled && this.authAdaptor.isAdmin)) {
      return of(true);
    }

    this.customProcessingFeeRateControl.markAsTouched();
    this.customProcessingFeeControl.markAsTouched();

    const isValid = !this.customProcessingFeeRateControl.invalid && !this.customProcessingFeeControl.invalid;
    return of(isValid);
  }

  private static _fromPaymentFlowToFeeType(paymentFlow: PaymentFlow): PaymentFeeType {
    return paymentFlow === PaymentFlow.STANDARD
      ? PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE
      : PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE;
  }

  private _getPaymentFeeRateSelector(
    selectedPaymentMethod: AbstractPaymentMethod,
    currency: Currency,
    paymentFeeType: PaymentFeeType = PaymentSummaryComponent._fromPaymentFlowToFeeType(this.paymentFlow),
  ): TypedSelector<number> {
    return PaymentsState.getPaymentFeeRate(
      paymentFeeType,
      PaymentsState.getPaymentFeePaymentMethodType(selectedPaymentMethod),
      currency.code,
    );
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
